import { useEffect, useMemo, useState } from "react";
import { categories, platformMap, platforms } from "../data/platforms";
import type { CategoryId, GuestState } from "../types";
import { Icon } from "./Icon";
import { PlatformLogo } from "./PlatformLogo";

interface SidebarProps {
  open: boolean;
  selectedPlatform: string | null;
  guestState: GuestState;
  onClose: () => void;
  onOpenLibrary: (tab: "favorites" | "history") => void;
  onSelectPlatform: (platformId: string | null) => void;
  onChangeGuestState: (state: GuestState) => void;
}

export function Sidebar({
  open,
  selectedPlatform,
  guestState,
  onClose,
  onOpenLibrary,
  onSelectPlatform,
  onChangeGuestState,
}: SidebarProps) {
  const [managing, setManaging] = useState(false);
  const [mobileLayout, setMobileLayout] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 820px)").matches);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 820px)");
    const update = () => setMobileLayout(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  const orderedPlatforms = useMemo(
    () => guestState.platformOrder.map((id) => platformMap.get(id)).filter((item) => item !== undefined),
    [guestState.platformOrder],
  );

  function toggleSubscription(platformId: string) {
    const subscriptions = guestState.subscriptions.includes(platformId)
      ? guestState.subscriptions.filter((id) => id !== platformId)
      : [...guestState.subscriptions, platformId];
    onChangeGuestState({ ...guestState, subscriptions });
  }

  function movePlatform(platformId: string, direction: -1 | 1) {
    const currentIndex = guestState.platformOrder.indexOf(platformId);
    const nextIndex = currentIndex + direction;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= guestState.platformOrder.length) return;
    const nextOrder = [...guestState.platformOrder];
    [nextOrder[currentIndex], nextOrder[nextIndex]] = [nextOrder[nextIndex], nextOrder[currentIndex]];
    onChangeGuestState({ ...guestState, platformOrder: nextOrder });
  }

  const groups = categories
    .filter((category): category is { id: CategoryId; label: string } => category.id !== "all")
    .map((category) => ({
      ...category,
      platforms: orderedPlatforms.filter(
        (platform) =>
          platform.category === category.id &&
          (managing || guestState.subscriptions.includes(platform.id)),
      ),
    }));

  return (
    <>
      {open ? <button aria-label="关闭平台菜单" className="sidebar-scrim is-visible" onClick={onClose} type="button" /> : null}
      <aside
        aria-hidden={mobileLayout && !open ? true : undefined}
        aria-label="平台导航"
        className={`sidebar ${open ? "is-open" : ""}`}
        inert={mobileLayout && !open ? true : undefined}
      >
        <div className="brand-row">
          <button
            aria-label="显示全部榜单"
            className="brand"
            onClick={() => {
              onSelectPlatform(null);
              onClose();
            }}
            type="button"
          >
            <span className="brand-mark" aria-hidden="true">聚</span>
            <span>
              <strong>今日聚合</strong>
              <small>{platforms.length} 个平台 · 10 分钟更新</small>
            </span>
          </button>
          <button className="icon-button mobile-only" aria-label="关闭菜单" onClick={onClose} type="button">
            <Icon name="close" />
          </button>
        </div>

        <button
          className={`platform-row all-platforms ${selectedPlatform === null ? "is-active" : ""}`}
          onClick={() => {
            onSelectPlatform(null);
            onClose();
          }}
          type="button"
        >
          <span className="platform-monogram total-monogram">{platforms.length}</span>
          <span className="platform-name">全部榜单</span>
          <span className="platform-count">总览</span>
        </button>

        <div className="sidebar-scroll">
          {groups.map((group) => (
            <section className="platform-group" key={group.id}>
              <h2>{group.label}</h2>
              {group.platforms.length ? (
                group.platforms.map((platform) => {
                  const subscribed = guestState.subscriptions.includes(platform.id);
                  return (
                    <div className="platform-manage-row" key={platform.id}>
                      <button
                        className={`platform-row ${selectedPlatform === platform.id ? "is-active" : ""} ${subscribed ? "" : "is-muted"}`}
                        onClick={() => {
                          if (managing) {
                            toggleSubscription(platform.id);
                          } else {
                            onSelectPlatform(platform.id);
                            onClose();
                          }
                        }}
                        type="button"
                      >
                        <PlatformLogo platform={platform} />
                        <span className="platform-name">{platform.name}</span>
                        {managing ? <span className="subscription-state">{subscribed ? "显示" : "隐藏"}</span> : null}
                      </button>
                      {managing ? (
                        <span className="reorder-buttons">
                          <button aria-label={`上移${platform.name}`} onClick={() => movePlatform(platform.id, -1)} type="button">
                            <Icon name="chevronUp" />
                          </button>
                          <button aria-label={`下移${platform.name}`} onClick={() => movePlatform(platform.id, 1)} type="button">
                            <Icon name="chevronDown" />
                          </button>
                        </span>
                      ) : null}
                    </div>
                  );
                })
              ) : (
                <p className="empty-group">该分组暂未显示平台</p>
              )}
            </section>
          ))}
        </div>

        <div className="sidebar-footer">
          <div className="library-shortcuts">
            <button onClick={() => { onOpenLibrary("favorites"); onClose(); }} type="button">
              <Icon name="bookmark" />收藏
            </button>
            <button onClick={() => { onOpenLibrary("history"); onClose(); }} type="button">
              <Icon name="clock" />历史
            </button>
          </div>
          <button className="manage-button" onClick={() => setManaging((value) => !value)} type="button">
            <Icon name={managing ? "check" : "settings"} />
            {managing ? "完成管理" : "管理平台"}
          </button>
          <p>榜单内容来自各平台公开页面与订阅源</p>
        </div>
      </aside>
    </>
  );
}

export const platformCount = platforms.length;
